'use client';
import PostCard from '@/components/PostCard';
import {
  fetchAds,
  fetchPostsSubscribedBuskers,
  fetchPostsSubscribedPlaces,
  fetchSuggestions,
} from '@/services/supabase-service';
import { Post } from '@/types/type';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { router } from 'expo-router';
import { useEffect, useState } from 'react';
import { FlatList, Text, TouchableOpacity } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

export default function Feed() {
  const [posts, setPosts] = useState<Post[]>([]);
  const [buskerSubscriptionsHasMore, setBuskerHasMore] = useState(true);
  const [placeSubscriptionsHasMore, setPlaceHasMore] = useState(true);
  const [adsHasMore, setAdsHasMore] = useState(true);
  const [adsOffset, setAdsOffset] = useState(0);
  const [suggestionsHasMore, setSuggestionsHasMore] = useState(true);
  const [suggestionsOffset, setSuggestionsOffset] = useState(0);
  const [buskerSubscriptionsOffset, setBuskerSubscriptionsOffset] = useState(0);
  const [placeSubscriptionsOffset, setPlaceSubscriptionsOffset] = useState(0);

  const PAGE_SIZE = 10;

  const [refreshing, setRefreshing] = useState(false);

  const handleRefresh = async () => {
    console.log('handleRefresh');
    setRefreshing(true);
    const [placesPosts, buskersPosts, adsPosts, suggestionsPosts] = await Promise.all([
      fetchPostsSubscribedPlaces(PAGE_SIZE, 0),
      fetchPostsSubscribedBuskers(PAGE_SIZE, 0),
      fetchAds(PAGE_SIZE, 0),
      fetchSuggestions(PAGE_SIZE, 0),
    ]);
    setBuskerHasMore(buskersPosts.length === PAGE_SIZE);
    setPlaceHasMore(placesPosts.length === PAGE_SIZE);
    setAdsHasMore(adsPosts.length === PAGE_SIZE);
    setSuggestionsHasMore(suggestionsPosts.length === PAGE_SIZE);
    setBuskerSubscriptionsOffset((prev) => buskersPosts.length);
    setPlaceSubscriptionsOffset((prev) => placesPosts.length);
    const combinedData = [...(placesPosts ?? []), ...(buskersPosts ?? [])];
    const uniqueData = Array.from(new Map(combinedData.map((item) => [item.id, item])).values());
    uniqueData.sort(
      (a, b) => new Date(b.created_at || '').getTime() - new Date(a.created_at || '').getTime()
    );

    // Insert suggestions every 3 positions
    var insertedSuggestions = 0 ;
    for (let i = 0; i < suggestionsPosts.length; i++) {
      const insertPosition = (i + 1) * 3;
      if (insertPosition <= uniqueData.length) {
        uniqueData.splice(insertPosition, 0, suggestionsPosts[i]);
        console.log('inserted suggestion at', insertPosition, suggestionsPosts[i]);
      } else {
        uniqueData.push(suggestionsPosts[i]);
      }
      insertedSuggestions++;
    }

    // Remove first insertedSuggestions elements from suggestionsPosts
    suggestionsPosts.splice(0, insertedSuggestions);

    // Insert ads every 5 positions
    var insertedAds = 0;
    for (let i = 0; i < adsPosts.length; i++) {
      const insertPosition = (i + 1) * 5;
      if (insertPosition <= uniqueData.length) {
        uniqueData.splice(insertPosition, 0, adsPosts[i]);
        console.log('inserted ad at', insertPosition, adsPosts[i]);
        insertedAds++;
      }
    }
    // Remove first insertedAds elements from adsPosts
    adsPosts.splice(0, insertedAds);


    setAdsOffset(adsPosts.length);
    setSuggestionsOffset(suggestionsPosts.length);
    setPosts(uniqueData);
    setRefreshing(false);
  };

  const loadMore = async () => {
    console.log('loadMore', {
      refreshing,
      buskerSubscriptionsHasMore,
      placeSubscriptionsHasMore,
      buskerSubscriptionsOffset,
      placeSubscriptionsOffset,
    });
    if (!refreshing && (buskerSubscriptionsHasMore || placeSubscriptionsHasMore)) {
      setRefreshing(true);
      const [places, buskers] = await Promise.all([
        placeSubscriptionsHasMore
          ? fetchPostsSubscribedPlaces(PAGE_SIZE, placeSubscriptionsOffset)
          : Promise.resolve([]),
        buskerSubscriptionsHasMore
          ? fetchPostsSubscribedBuskers(PAGE_SIZE, buskerSubscriptionsOffset)
          : Promise.resolve([]),
      ]);
      setBuskerHasMore(buskers.length > 0);
      setPlaceHasMore(places.length > 0);
      setBuskerSubscriptionsOffset((prev) => buskers.length + prev);
      setPlaceSubscriptionsOffset((prev) => places.length + prev);
      const combinedData = [...(places ?? []), ...(buskers ?? [])];
      const uniqueData = Array.from(new Map(combinedData.map((item) => [item.id, item])).values());
      uniqueData.sort(
        (a, b) => new Date(b.created_at || '').getTime() - new Date(a.created_at || '').getTime()
      );
      const filteredData = uniqueData.filter((item) => !posts.some((post) => post.id === item.id));
      setPosts((prev) => [...prev, ...filteredData]);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    console.log('initial load');
    handleRefresh();
  }, []);

  return (
    <SafeAreaView className="h-full w-full">
      <FlatList
        data={posts}
        renderItem={({ item }) => <PostCard key={item.id} post={item} />}
        keyExtractor={(item, index) => item.id?.toString() || index.toString()}
        onEndReached={loadMore}
        onEndReachedThreshold={0}
        refreshing={refreshing}
        onRefresh={handleRefresh}
        ListFooterComponent={
          !buskerSubscriptionsHasMore && !placeSubscriptionsHasMore ? (
            <Text>nothing more</Text>
          ) : null
        }
      />

      <TouchableOpacity
        onPress={() => router.push('./../post-create')}
        className="absolute bottom-0 right-0 p-4 bg-blue-500 rounded-full">
        <MaterialCommunityIcons name="plus" size={24} />
      </TouchableOpacity>
    </SafeAreaView>
  );
}
